/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import type { Context } from '../context/types';
import { NoopTracer } from './NoopTracer';
import type { Span } from './span';
import type { SpanOptions } from './SpanOptions';
import type { Tracer } from './tracer';
import type { TracerOptions } from './tracer_options';

const NOOP_TRACER = new NoopTracer();

/**
 * Proxy tracer provided by the proxy tracer provider
 *
 * @since 1.0.0
 */
export class ProxyTracer implements Tracer {
  // When a real implementation is provided, this will be it
  private _delegate?: Tracer;
  private _provider: TracerDelegator;
  public readonly name: string;
  public readonly version?: string;
  /** @since 1.1.0 */
  public readonly options?: TracerOptions;

  constructor(
    provider: TracerDelegator,
    name: string,
    version?: string,
    options?: TracerOptions
  ) {
    this._provider = provider;
    this.name = name;
    this.version = version;
    this.options = options;
  }

  startSpan(name: string, options?: SpanOptions, context?: Context): Span {
    return this._getTracer().startSpan(name, options, context);
  }

  startActiveSpan<F extends (span: Span) => unknown>(
    _name: string,
    _options: F | SpanOptions,
    _context?: F | Context,
    _fn?: F
  ): ReturnType<F> {
    const tracer = this._getTracer();
    return Reflect.apply(tracer.startActiveSpan, tracer, arguments);
  }

  /**
   * Try to get a tracer from the proxy tracer provider.
   * If the proxy tracer provider has no delegate, return a noop tracer.
   */
  private _getTracer() {
    if (this._delegate) {
      return this._delegate;
    }

    const tracer = this._provider.getDelegateTracer(
      this.name,
      this.version,
      this.options
    );

    if (!tracer) {
      return NOOP_TRACER;
    }

    this._delegate = tracer;
    return this._delegate;
  }
}

/**
 * @since 1.0.3
 */
export interface TracerDelegator {
  getDelegateTracer(
    name: string,
    version?: string,
    options?: TracerOptions
  ): Tracer | undefined;
}
